// react
import { useContext, useMemo, useState } from 'react';

// router
import { useNavigate } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject } from '../../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// mui
import {
	alpha,
	Avatar,
	Box,
	Card,
	CardContent,
	Chip,
	Divider,
	Grid,
	IconButton,
	InputAdornment,
	LinearProgress,
	Paper,
	Stack,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	AccountTree as IconActivity,
	AttachMoney as IconMoney,
	CalendarMonth as IconCalendar,
	CheckCircle as IconComplete,
	ChevronLeft as IconChevronLeft,
	ChevronRight as IconChevronRight,
	Close as IconClose,
	Description as IconContract,
	OpenInNew as IconOpen,
	Payments as IconPayments,
	Search as IconSearch,
	Timeline as IconTimeline,
	ViewTimeline as IconGantt,
} from '@mui/icons-material';

// recharts
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Legend,
	Line,
	LineChart,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip as RechartsTooltip,
	XAxis,
	YAxis,
} from 'recharts';

// components
import { PaginationBar } from '../../../ui/SearchFilterBar/SearchFilterBar';
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';

// data

// ui
import { Empty } from '../../../ui/Empty/Empty';

// utils
import { formatCurrency } from '../../../../utils/currency';

// types
import { PlanningActivity, V2Contract, Voucher } from '../../../../@types';

// Types

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

interface ActivityWithPayments extends PlanningActivity {
	contracts: V2Contract[];
	vouchers: Voucher[];
	totalContractAmount: number;
	totalPaid: number;
	paymentProgress: number;
}

// Gantt chart view type
type GanttViewScale = 'days' | 'weeks' | 'months' | 'quarters' | 'years';

// Tab Panel Component

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`activity-monitoring-tabpanel-${index}`}
			aria-labelledby={`activity-monitoring-tab-${index}`}
			{...other}>
			{value === index && <Box>{children}</Box>}
		</div>
	);
};

// Activity Monitoring Component

export const ActivityMonitoring = () => {
	// hooks
	const navigate = useNavigate();
	const { t } = useTranslation();
	const theme = useTheme();

	// context
	const { project } = useContext(ContextProject);
	const { activities, contracts, vouchers, workPlans } = useContext(ContextSpendPlan);

	// state
	const [tabValue, setTabValue] = useState(0);
	const [searchQuery, setSearchQuery] = useState('');
	const [page, setPage] = useState(1);
	const [ganttViewScale, setGanttViewScale] = useState<GanttViewScale>('months');
	const rowsPerPage = 10;

	// Get default currency from project or first activity
	const defaultCurrency = activities[0]?.currency || 'USD';

	// handlers
	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		setTabValue(newValue);
	};

	// Memoized calculations
	const activitiesWithPayments = useMemo((): ActivityWithPayments[] => {
		return activities.map((activity) => {
			// Find all contracts linked to this activity
			const activityContracts = contracts.filter((c) => c.planningActivity === activity.id);

			// Find all vouchers (payments) for these contracts
			const activityVouchers = activityContracts.flatMap((contract) =>
				vouchers.filter((v) => {
					const voucherContract = typeof v.contract === 'object' ? v.contract?.id : v.contract;
					return voucherContract === contract.id;
				})
			);

			// Calculate totals
			const totalContractAmount = activityContracts.reduce((sum, c) => {
				const primaryAmount = c.amounts?.find((a) => a.currency === defaultCurrency);
				return sum + (primaryAmount?.amount || 0);
			}, 0);

			const totalPaid = activityVouchers.reduce((sum, v) => sum + (v.amount || 0), 0);

			return {
				...activity,
				contracts: activityContracts,
				vouchers: activityVouchers,
				totalContractAmount,
				totalPaid,
				paymentProgress: totalContractAmount > 0 ? (totalPaid / totalContractAmount) * 100 : 0,
			};
		});
	}, [activities, contracts, vouchers, defaultCurrency]);

	// Filtered activities
	const filteredActivities = useMemo(() => {
		if (!searchQuery) return activitiesWithPayments;
		const query = searchQuery.toLowerCase();
		return activitiesWithPayments.filter(
			(activity) =>
				activity.name.toLowerCase().includes(query) ||
				activity.description?.toLowerCase().includes(query)
		);
	}, [activitiesWithPayments, searchQuery]);

	// Paginated activities
	const paginatedActivities = useMemo(() => {
		const startIndex = (page - 1) * rowsPerPage;
		return filteredActivities.slice(startIndex, startIndex + rowsPerPage);
	}, [filteredActivities, page, rowsPerPage]);

	// Summary statistics
	const summaryStats = useMemo(() => {
		const totalActivities = activities.length;
		const activeActivities = activities.filter((a) => a.status === 'IN_PROGRESS').length;
		const completedActivities = activities.filter((a) => a.status === 'COMPLETED').length;
		const totalBudget = activities.reduce((sum, a) => sum + (a.amount || 0), 0);
		const totalPaid = activitiesWithPayments.reduce((sum, a) => sum + a.totalPaid, 0);
		const totalContracts = contracts.length;

		return {
			totalActivities,
			activeActivities,
			completedActivities,
			totalBudget,
			totalPaid,
			totalContracts,
		};
	}, [activities, activitiesWithPayments, contracts]);

	// Timeline data for charts
	const timelineData = useMemo(() => {
		// Group activities by month
		const monthlyData: Record<
			string,
			{ month: string; budget: number; spent: number; activities: number }
		> = {};

		activitiesWithPayments.forEach((activity) => {
			const startDate = activity.dateStart ? new Date(activity.dateStart) : new Date();
			const monthKey = startDate.toISOString().slice(0, 7); // YYYY-MM format
			const monthLabel = startDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

			if (!monthlyData[monthKey]) {
				monthlyData[monthKey] = { month: monthLabel, budget: 0, spent: 0, activities: 0 };
			}
			monthlyData[monthKey].budget += activity.amount || 0;
			monthlyData[monthKey].spent += activity.totalPaid;
			monthlyData[monthKey].activities += 1;
		});

		return Object.values(monthlyData)
			.sort((a, b) => a.month.localeCompare(b.month))
			.slice(-12);
	}, [activitiesWithPayments]);

	// Status distribution for pie chart
	const statusDistribution = useMemo(() => {
		const distribution: Record<string, number> = {};
		activities.forEach((activity) => {
			const status = activity.status || 'PLANNED';
			distribution[status] = (distribution[status] || 0) + 1;
		});
		return Object.entries(distribution).map(([name, value]) => ({ name, value }));
	}, [activities]);

	// Color mapping for status
	const getStatusColor = (status: string) => {
		switch (status) {
			case 'IN_PROGRESS':
				return theme.palette.info.main;
			case 'COMPLETED':
				return theme.palette.success.main;
			case 'PLANNED':
				return theme.palette.warning.main;
			case 'CANCELLED':
				return theme.palette.error.main;
			case 'DELAYED':
				return theme.palette.error.light;
			default:
				return theme.palette.grey[500];
		}
	};

	const getStatusLabel = (status: string, t: (key: string) => string) => {
		switch (status) {
			case 'IN_PROGRESS':
				return t('inProgress');
			case 'COMPLETED':
				return t('completed');
			case 'PLANNED':
				return t('planned');
			case 'CANCELLED':
				return t('cancelled');
			case 'DELAYED':
				return t('delayed');
			default:
				return status;
		}
	};

	const getVoucherStatusLabel = (status: string) => {
		switch (status) {
			case 'READY':
				return 'Ready';
			case 'VERIFIED':
				return 'Verified';
			case 'REDEEMED':
				return 'Redeemed';
			case 'GENERATED':
				return 'Generated';
			case 'CANCELLED':
				return 'Cancelled';
			default:
				return status;
		}
	};

	// Gantt chart helpers
	const ganttData = useMemo(() => {
		// Group activities by status for Gantt view
		const statusGroups: Record<string, ActivityWithPayments[]> = {};

		activitiesWithPayments.forEach((activity) => {
			const status = activity.status || 'PLANNED';
			if (!statusGroups[status]) {
				statusGroups[status] = [];
			}
			statusGroups[status].push(activity);
		});

		return statusGroups;
	}, [activitiesWithPayments]);

	const ganttTimeRange = useMemo(() => {
		let minDate = new Date();
		let maxDate = new Date();
		let hasData = false;

		activitiesWithPayments.forEach((activity) => {
			if (activity.dateStart) {
				const start = new Date(activity.dateStart);
				if (!hasData || start < minDate) minDate = new Date(start);
				hasData = true;
			}
			if (activity.dateEnd) {
				const end = new Date(activity.dateEnd);
				if (!hasData || end > maxDate) maxDate = new Date(end);
				hasData = true;
			}
		});

		// Normalize to start/end of month for cleaner display
		minDate = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
		maxDate = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0); // Last day of month

		return { minDate, maxDate };
	}, [activitiesWithPayments]);

	const getGanttBarPosition = (startDate: Date, endDate: Date) => {
		const { minDate, maxDate } = ganttTimeRange;
		const totalDuration = maxDate.getTime() - minDate.getTime();
		const startOffset = ((startDate.getTime() - minDate.getTime()) / totalDuration) * 100;
		const width = ((endDate.getTime() - startDate.getTime()) / totalDuration) * 100;
		return { left: `${Math.max(0, startOffset)}%`, width: `${Math.max(1, width)}%` };
	};

	const formatGanttDate = (date: Date) => {
		return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
	};

	const getGanttTimelineHeaders = () => {
		const { minDate, maxDate } = ganttTimeRange;
		const headers: { label: string; subLabel?: string; width: string }[] = [];
		const currentDate = new Date(minDate);

		while (currentDate <= maxDate) {
			let label = '';
			let subLabel = '';
			const nextDate = new Date(currentDate);

			switch (ganttViewScale) {
				case 'days':
					label = currentDate.toLocaleDateString('en-US', { month: 'short' });
					subLabel = currentDate.getDate().toString();
					nextDate.setDate(nextDate.getDate() + 1);
					break;
				case 'weeks':
					label = currentDate.toLocaleDateString('en-US', { month: 'short' });
					subLabel = `W${Math.ceil(currentDate.getDate() / 7)}`;
					nextDate.setDate(nextDate.getDate() + 7);
					break;
				case 'months':
					label = currentDate.toLocaleDateString('en-US', { month: 'short' });
					subLabel = currentDate.toLocaleDateString('en-US', { year: '2-digit' });
					nextDate.setMonth(nextDate.getMonth() + 1);
					break;
				case 'quarters': {
					const quarter = Math.ceil((currentDate.getMonth() + 1) / 3);
					label = `Q${quarter}`;
					subLabel = currentDate.getFullYear().toString().slice(-2);
					nextDate.setMonth(nextDate.getMonth() + 3);
					break;
				}
				case 'years':
					label = currentDate.getFullYear().toString();
					nextDate.setFullYear(nextDate.getFullYear() + 1);
					break;
			}

			const totalDuration = maxDate.getTime() - minDate.getTime();
			const periodEnd = Math.min(nextDate.getTime(), maxDate.getTime() + 1);
			const periodDuration = periodEnd - currentDate.getTime();
			const width = Math.max((periodDuration / totalDuration) * 100, 0);

			if (width > 0) {
				headers.push({ label, subLabel, width: `${width}%` });
			}
			currentDate.setTime(nextDate.getTime());
		}

		return headers;
	};

	const getDurationDays = (startDate: Date, endDate: Date) => {
		const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
		return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
	};

	// Render Gantt chart tab
	const renderGanttTab = () => {
		const timelineHeaders = getGanttTimelineHeaders();
		const statusOrder = ['IN_PROGRESS', 'PLANNED', 'DELAYED', 'COMPLETED', 'CANCELLED'];
		const sortedStatuses = Object.keys(ganttData).sort(
			(a, b) => statusOrder.indexOf(a) - statusOrder.indexOf(b)
		);

		return (
			<Box>
				{/* Timeline View Controls */}
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
					<Typography variant="h6" fontWeight={600}>
						{t('spendPlan.activityMonitoring.timelineView')}
					</Typography>
					<Stack direction="row" spacing={2} alignItems="center">
						<Stack direction="row" spacing={1} alignItems="center">
							<IconButton
								size="small"
								onClick={() => {
									const scales: GanttViewScale[] = ['days', 'weeks', 'months', 'quarters', 'years'];
									const currentIndex = scales.indexOf(ganttViewScale);
									if (currentIndex > 0) setGanttViewScale(scales[currentIndex - 1]);
								}}>
								<IconChevronLeft />
							</IconButton>
							<Chip
								label={ganttViewScale.charAt(0).toUpperCase() + ganttViewScale.slice(1)}
								variant="outlined"
								sx={{ minWidth: 80 }}
							/>
							<IconButton
								size="small"
								onClick={() => {
									const scales: GanttViewScale[] = ['days', 'weeks', 'months', 'quarters', 'years'];
									const currentIndex = scales.indexOf(ganttViewScale);
									if (currentIndex < scales.length - 1) setGanttViewScale(scales[currentIndex + 1]);
								}}>
								<IconChevronRight />
							</IconButton>
						</Stack>
						<Chip
							label={t('spendPlan.activityMonitoring.autoFit')}
							variant="outlined"
							size="small"
							sx={{ cursor: 'pointer' }}
						/>
						<Chip
							label={t('spendPlan.activityMonitoring.baseline')}
							variant="outlined"
							size="small"
							sx={{ cursor: 'pointer' }}
						/>
					</Stack>
				</Stack>

				{/* Gantt Chart Container */}
				<Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
					{/* Timeline Header */}
					<Box
						sx={{
							display: 'flex',
							borderBottom: `1px solid ${theme.palette.divider}`,
							bgcolor: alpha(theme.palette.primary.main, 0.05),
						}}>
						<Box
							sx={{
								minWidth: 300,
								maxWidth: 300,
								p: 2,
								borderRight: `1px solid ${theme.palette.divider}`,
								fontWeight: 600,
							}}>
							{t('spendPlan.activityMonitoring.activity')}
						</Box>
						<Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
							{timelineHeaders.map((header, index) => (
								<Box
									key={index}
									sx={{
										flex: `0 0 ${header.width}`,
										py: 1,
										px: 0.5,
										textAlign: 'center',
										borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
										bgcolor: alpha(theme.palette.primary.main, 0.08),
										minWidth: 40,
									}}>
									<Typography variant="caption" fontWeight={600} color="primary" display="block">
										{header.label}
									</Typography>
									{header.subLabel && (
										<Typography
											variant="caption"
											color="primary.dark"
											display="block"
											sx={{ fontSize: '0.65rem' }}>
											{header.subLabel}
										</Typography>
									)}
								</Box>
							))}
						</Box>
					</Box>

					{/* Gantt Rows by Status Group */}
					{sortedStatuses.map((status) => {
						const statusActivities = ganttData[status];
						if (!statusActivities || statusActivities.length === 0) return null;

						return (
							<Box key={status}>
								{/* Status Group Header */}
								<Box
									sx={{
										display: 'flex',
										borderBottom: `1px solid ${theme.palette.divider}`,
										bgcolor: alpha(getStatusColor(status), 0.08),
									}}>
									<Box
										sx={{
											minWidth: 300,
											maxWidth: 300,
											p: 1.5,
											borderRight: `1px solid ${theme.palette.divider}`,
										}}>
										<Stack direction="row" alignItems="center" spacing={1}>
											<Box
												sx={{
													width: 12,
													height: 12,
													borderRadius: '50%',
													bgcolor: getStatusColor(status),
												}}
											/>
											<Typography
												variant="subtitle2"
												fontWeight={600}
												color={getStatusColor(status)}>
												{getStatusLabel(status, t)}
											</Typography>
										</Stack>
									</Box>
									<Box sx={{ flex: 1, p: 1.5 }}>
										<Stack direction="row" alignItems="center" spacing={1}>
											<Typography variant="caption" fontWeight={600} color={getStatusColor(status)}>
												{getStatusLabel(status, t)}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												●
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{statusActivities.length > 0 &&
													statusActivities[0].dateStart &&
													statusActivities[0].dateEnd && (
														<>
															{formatGanttDate(new Date(statusActivities[0].dateStart))} -{' '}
															{formatGanttDate(
																new Date(
																	statusActivities[statusActivities.length - 1].dateEnd ||
																		statusActivities[0].dateEnd
																)
															)}
														</>
													)}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												● {statusActivities.length} {t('spendPlan.activityMonitoring.activities')}
											</Typography>
										</Stack>
									</Box>
								</Box>

								{/* Activity Rows */}
								{[...statusActivities]
									.sort((a, b) => {
										const dateA = a.dateStart ? new Date(a.dateStart).getTime() : 0;
										const dateB = b.dateStart ? new Date(b.dateStart).getTime() : 0;
										return dateA - dateB;
									})
									.map((activity) => {
										const startDate = activity.dateStart
											? new Date(activity.dateStart)
											: new Date();
										const endDate = activity.dateEnd
											? new Date(activity.dateEnd)
											: new Date(startDate.getTime() + 30 * 24 * 60 * 60 * 1000);
										const barPosition = getGanttBarPosition(startDate, endDate);
										const duration = getDurationDays(startDate, endDate);

										return (
											<Box
												key={activity.id}
												sx={{
													display: 'flex',
													borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
													'&:hover': {
														bgcolor: alpha(theme.palette.primary.main, 0.04),
													},
												}}>
												{/* Activity Name Column */}
												<Box
													sx={{
														minWidth: 300,
														maxWidth: 300,
														p: 1.5,
														borderRight: `1px solid ${theme.palette.divider}`,
														display: 'flex',
														alignItems: 'center',
													}}>
													<Stack direction="row" spacing={2} alignItems="center" sx={{ pl: 2 }}>
														<Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
															{activity.name}
														</Typography>
														<Typography variant="caption" color="text.secondary" noWrap>
															{formatGanttDate(startDate)} - {formatGanttDate(endDate)}
														</Typography>
													</Stack>
												</Box>

												{/* Gantt Bar Column */}
												<Box
													sx={{
														flex: 1,
														position: 'relative',
														minHeight: 48,
														display: 'flex',
														alignItems: 'center',
													}}>
													{/* Bar */}
													<Tooltip
														componentsProps={{
															tooltip: {
																sx: {
																	bgcolor: 'background.paper',
																	color: 'text.primary',
																	boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
																	borderRadius: 2,
																	p: 0,
																	minWidth: 220,
																	border: `1px solid ${theme.palette.divider}`,
																},
															},
															arrow: {
																sx: {
																	color: 'background.paper',
																	'&::before': {
																		border: `1px solid ${theme.palette.divider}`,
																	},
																},
															},
														}}
														title={
															<Box>
																{/* Header */}
																<Box
																	sx={{
																		bgcolor: alpha(getStatusColor(status), 0.1),
																		borderBottom: `1px solid ${theme.palette.divider}`,
																		p: 1.5,
																	}}>
																	<Typography
																		variant="subtitle2"
																		fontWeight={600}
																		color={getStatusColor(status)}>
																		{activity.name}
																	</Typography>
																</Box>
																{/* Content */}
																<Box sx={{ p: 1.5 }}>
																	<Stack spacing={1}>
																		<Box>
																			<Typography variant="caption" color="text.secondary">
																				{formatGanttDate(startDate)} - {formatGanttDate(endDate)} (
																				{duration} {t('spendPlan.activityMonitoring.days')})
																			</Typography>
																		</Box>
																		<Divider />
																		<Stack direction="row" justifyContent="space-between">
																			<Typography variant="caption" color="text.secondary">
																				{t('spendPlan.activityMonitoring.tooltipBudget')}:
																			</Typography>
																			<Typography variant="caption" fontWeight={600}>
																				{formatCurrency({
																					value: activity.amount,
																					currency: activity.currency,
																				})}
																			</Typography>
																		</Stack>
																		<Stack direction="row" justifyContent="space-between">
																			<Typography variant="caption" color="text.secondary">
																				{t('spendPlan.activityMonitoring.tooltipPaid')}:
																			</Typography>
																			<Typography
																				variant="caption"
																				fontWeight={600}
																				color="success.main">
																				{formatCurrency({
																					value: activity.totalPaid,
																					currency: activity.currency,
																				})}{' '}
																				({activity.paymentProgress.toFixed(1)}%)
																			</Typography>
																		</Stack>
																	</Stack>
																</Box>
															</Box>
														}
														arrow>
														<Box
															sx={{
																position: 'absolute',
																left: barPosition.left,
																width: barPosition.width,
																height: 28,
																bgcolor: getStatusColor(status),
																borderRadius: 1,
																display: 'flex',
																alignItems: 'center',
																px: 1,
																cursor: 'pointer',
																transition: 'transform 0.2s, box-shadow 0.2s',
																'&:hover': {
																	transform: 'scaleY(1.1)',
																	boxShadow: `0 2px 8px ${alpha(getStatusColor(status), 0.4)}`,
																},
															}}
															onClick={() =>
																navigate(
																	`/project/${project?.idWbg}/spend-plans/activity/${activity.id}`
																)
															}>
															<Typography
																variant="caption"
																sx={{
																	color: 'common.white',
																	fontWeight: 500,
																	whiteSpace: 'nowrap',
																	overflow: 'hidden',
																	textOverflow: 'ellipsis',
																}}>
																{activity.name}
															</Typography>
														</Box>
													</Tooltip>
												</Box>
											</Box>
										);
									})}
							</Box>
						);
					})}

					{/* Empty State */}
					{sortedStatuses.length === 0 && (
						<Box sx={{ p: 4 }}>
							<Empty
								IconCircleProps={{ icon: <IconGantt /> }}
								content={{ children: t('spendPlan.activityMonitoring.empty.description') }}
							/>
						</Box>
					)}
				</Paper>

				{/* Legend */}
				<Stack direction="row" spacing={3} justifyContent="center" sx={{ mt: 3 }}>
					{statusDistribution.map((item) => (
						<Stack key={item.name} direction="row" alignItems="center" spacing={1}>
							<Box
								sx={{
									width: 12,
									height: 12,
									borderRadius: '50%',
									bgcolor: getStatusColor(item.name),
								}}
							/>
							<Typography variant="caption" color="text.secondary">
								{getStatusLabel(item.name, t)}
							</Typography>
						</Stack>
					))}
				</Stack>
			</Box>
		);
	};

	// Render summary cards
	const renderSummaryCards = () => (
		<Grid container spacing={2} sx={{ mb: 3 }}>
			<Grid item xs={12} sm={6} md={4} lg={2}>
				<Card
					sx={{
						height: '100%',
						borderRadius: 2,
						border: `1px solid ${theme.palette.divider}`,
					}}>
					<CardContent sx={{ p: 2 }}>
						<Stack direction="row" alignItems="center" spacing={2}>
							<Avatar
								sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), width: 40, height: 40 }}>
								<IconActivity sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
							</Avatar>
							<Box>
								<Typography
									variant="h5"
									sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
									{summaryStats.totalActivities}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{t('spendPlan.activityMonitoring.totalActivities')}
								</Typography>
							</Box>
						</Stack>
					</CardContent>
				</Card>
			</Grid>
			<Grid item xs={12} sm={6} md={4} lg={2}>
				<Card
					sx={{
						height: '100%',
						borderRadius: 2,
						border: `1px solid ${theme.palette.divider}`,
					}}>
					<CardContent sx={{ p: 2 }}>
						<Stack direction="row" alignItems="center" spacing={2}>
							<Avatar
								sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), width: 40, height: 40 }}>
								<IconComplete sx={{ color: theme.palette.success.main, fontSize: 20 }} />
							</Avatar>
							<Box>
								<Typography
									variant="h5"
									sx={{ fontWeight: 700, color: theme.palette.success.main }}>
									{summaryStats.activeActivities}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{t('spendPlan.activityMonitoring.activeActivities')}
								</Typography>
							</Box>
						</Stack>
					</CardContent>
				</Card>
			</Grid>
			<Grid item xs={12} sm={6} md={4} lg={2}>
				<Card
					sx={{
						height: '100%',
						borderRadius: 2,
						border: `1px solid ${theme.palette.divider}`,
					}}>
					<CardContent sx={{ p: 2 }}>
						<Stack direction="row" alignItems="center" spacing={2}>
							<Avatar
								sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), width: 40, height: 40 }}>
								<IconContract sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
							</Avatar>
							<Box>
								<Typography
									variant="h5"
									sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
									{summaryStats.totalContracts}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{t('spendPlan.activityMonitoring.totalContracts')}
								</Typography>
							</Box>
						</Stack>
					</CardContent>
				</Card>
			</Grid>
			<Grid item xs={12} sm={6} md={4} lg={3}>
				<Card
					sx={{
						height: '100%',
						borderRadius: 2,
						border: `1px solid ${theme.palette.divider}`,
					}}>
					<CardContent sx={{ p: 2 }}>
						<Stack direction="row" alignItems="center" spacing={2}>
							<Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), width: 40, height: 40 }}>
								<IconMoney sx={{ color: theme.palette.info.main, fontSize: 20 }} />
							</Avatar>
							<Box>
								<Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
									{formatCurrency({ value: summaryStats.totalBudget, currency: defaultCurrency })}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{t('spendPlan.activityMonitoring.totalBudget')}
								</Typography>
							</Box>
						</Stack>
					</CardContent>
				</Card>
			</Grid>
			<Grid item xs={12} sm={6} md={4} lg={3}>
				<Card
					sx={{
						height: '100%',
						borderRadius: 2,
						border: `1px solid ${theme.palette.divider}`,
					}}>
					<CardContent sx={{ p: 2 }}>
						<Stack direction="row" alignItems="center" spacing={2}>
							<Avatar
								sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), width: 40, height: 40 }}>
								<IconPayments sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
							</Avatar>
							<Box>
								<Typography
									variant="h6"
									sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
									{formatCurrency({ value: summaryStats.totalPaid, currency: defaultCurrency })}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{t('spendPlan.activityMonitoring.totalPaid')}
								</Typography>
							</Box>
						</Stack>
					</CardContent>
				</Card>
			</Grid>
		</Grid>
	);

	// Render activities overview tab
	const renderOverviewTab = () => (
		<Box>
			{/* Search bar */}
			<Box sx={{ mb: 3 }}>
				<TextField
					placeholder={t('spendPlan.activityMonitoring.searchPlaceholder')}
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					size="small"
					sx={{
						minWidth: 300,
						maxWidth: 400,
						'& .MuiOutlinedInput-root': {
							backgroundColor: 'background.paper',
							'& fieldset': {
								borderColor: 'divider',
							},
						},
					}}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<IconSearch sx={{ color: 'text.disabled', fontSize: 20 }} />
							</InputAdornment>
						),
						endAdornment: searchQuery ? (
							<InputAdornment position="end">
								<IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
									<IconClose sx={{ fontSize: 18 }} />
								</IconButton>
							</InputAdornment>
						) : null,
					}}
				/>
			</Box>

			{/* Activities table with payment info */}
			<TableContainer component={Paper} sx={{ borderRadius: 2 }}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: 600 }}>
								{t('spendPlan.activityMonitoring.activity')}
							</TableCell>
							<TableCell sx={{ fontWeight: 600 }}>
								{t('spendPlan.activityMonitoring.workPlan')}
							</TableCell>
							<TableCell sx={{ fontWeight: 600 }}>
								{t('spendPlan.activityMonitoring.timeline')}
							</TableCell>
							<TableCell sx={{ fontWeight: 600 }}>
								{t('spendPlan.activityMonitoring.status')}
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 600 }}>
								{t('spendPlan.activityMonitoring.budget')}
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 600 }}>
								{t('spendPlan.activityMonitoring.contracts')}
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 600 }}>
								{t('spendPlan.activityMonitoring.paid')}
							</TableCell>
							<TableCell sx={{ fontWeight: 600 }}>
								{t('spendPlan.activityMonitoring.progress')}
							</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{paginatedActivities.length === 0 ? (
							<TableRow>
								<TableCell colSpan={9}>
									<Empty
										IconCircleProps={{ icon: <IconActivity /> }}
										content={{ children: t('spendPlan.activityMonitoring.empty.description') }}
									/>
								</TableCell>
							</TableRow>
						) : (
							paginatedActivities.map((activity) => {
								const workPlan =
									typeof activity.workPlan === 'object'
										? activity.workPlan
										: workPlans.find((wp) => wp.id === activity.workPlan);
								return (
									<TableRow
										key={activity.id}
										hover
										sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
										<TableCell>
											<Stack direction="row" alignItems="center" spacing={1.5}>
												<Avatar
													sx={{
														width: 36,
														height: 36,
														bgcolor: alpha(theme.palette.primary.main, 0.1),
													}}>
													<IconActivity sx={{ fontSize: 20, color: theme.palette.primary.main }} />
												</Avatar>
												<Box>
													<Typography variant="body2" fontWeight={600}>
														{activity.name}
													</Typography>
													{activity.description && (
														<Typography
															variant="caption"
															color="text.secondary"
															sx={{
																display: '-webkit-box',
																WebkitLineClamp: 1,
																WebkitBoxOrient: 'vertical',
																overflow: 'hidden',
															}}>
															{activity.description}
														</Typography>
													)}
												</Box>
											</Stack>
										</TableCell>
										<TableCell>
											<Typography variant="body2">{workPlan?.name || '-'}</Typography>
										</TableCell>
										<TableCell>
											<Stack direction="row" alignItems="center" spacing={1}>
												<IconCalendar sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
												<Typography variant="body2" color="text.secondary">
													{activity.dateStart
														? new Date(activity.dateStart).toLocaleDateString(undefined, {
																timeZone: 'UTC',
															})
														: '-'}
													{activity.dateEnd &&
														` - ${new Date(activity.dateEnd).toLocaleDateString(undefined, {
															timeZone: 'UTC',
														})}`}
												</Typography>
											</Stack>
										</TableCell>
										<TableCell>
											<Chip
												label={getStatusLabel(activity.status, t)}
												size="small"
												sx={{
													bgcolor: alpha(getStatusColor(activity.status), 0.1),
													color: getStatusColor(activity.status),
													fontWeight: 500,
												}}
											/>
										</TableCell>
										<TableCell align="right">
											<Typography variant="body2" fontWeight={500}>
												{formatCurrency({ value: activity.amount, currency: activity.currency })}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Chip
												label={String(activity.contracts.length)}
												size="small"
												color="info"
												variant="outlined"
											/>
										</TableCell>
										<TableCell align="right">
											<Typography
												variant="body2"
												fontWeight={500}
												color={activity.totalPaid > 0 ? 'success.main' : 'text.secondary'}>
												{formatCurrency({ value: activity.totalPaid, currency: activity.currency })}
											</Typography>
										</TableCell>
										<TableCell>
											<Stack spacing={0.5}>
												<LinearProgress
													variant="determinate"
													value={Math.min(activity.paymentProgress, 100)}
													sx={{
														height: 8,
														borderRadius: 4,
														bgcolor: alpha(theme.palette.primary.main, 0.1),
														'& .MuiLinearProgress-bar': {
															borderRadius: 4,
															bgcolor:
																activity.paymentProgress >= 100
																	? theme.palette.success.main
																	: theme.palette.primary.main,
														},
													}}
												/>
												<Typography variant="caption" color="text.secondary">
													{activity.paymentProgress.toFixed(1)}%
												</Typography>
											</Stack>
										</TableCell>
										<TableCell>
											<Tooltip title={t('spendPlan.activityMonitoring.viewDetails')}>
												<IconButton
													size="small"
													onClick={() =>
														navigate(
															`/project/${project?.idWbg}/spend-plans/activity/${activity.id}`
														)
													}>
													<IconOpen sx={{ fontSize: 18 }} />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Pagination */}
			{filteredActivities.length > rowsPerPage && (
				<Box sx={{ mt: 2 }}>
					<PaginationBar
						totalCount={filteredActivities.length}
						page={page}
						rowsPerPage={rowsPerPage}
						onPageChange={setPage}
						onRowsPerPageChange={() => {
							/** empty arrow */
						}}
					/>
				</Box>
			)}

			{/* Contract Payments Section */}
			<Box sx={{ mt: 4 }}>
				<Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
					{t('spendPlan.activityMonitoring.contractPayments')}
				</Typography>
				<TableContainer component={Paper} sx={{ borderRadius: 2 }}>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 600 }}>
									{t('spendPlan.activityMonitoring.activity')}
								</TableCell>
								<TableCell sx={{ fontWeight: 600 }}>
									{t('spendPlan.activityMonitoring.contract')}
								</TableCell>
								<TableCell sx={{ fontWeight: 600 }}>
									{t('spendPlan.activityMonitoring.paymentDate')}
								</TableCell>
								<TableCell align="right" sx={{ fontWeight: 600 }}>
									{t('spendPlan.activityMonitoring.paymentAmount')}
								</TableCell>
								<TableCell sx={{ fontWeight: 600 }}>
									{t('spendPlan.activityMonitoring.paymentStatus')}
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{activitiesWithPayments
								.flatMap((activity) =>
									activity.vouchers.map((voucher) => {
										const contractObj =
											typeof voucher.contract === 'object' ? voucher.contract : null;
										const contractId =
											typeof voucher.contract === 'number' ? voucher.contract : contractObj?.id;
										const contract = contracts.find((c) => c.id === contractId);
										return { activity, voucher, contract };
									})
								)
								.slice(0, 10)
								.map(({ activity, voucher, contract }, index) => (
									<TableRow key={`${activity.id}-${voucher.id}-${index}`} hover>
										<TableCell>
											<Typography variant="body2">{activity.name}</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2">
												{contract?.num || `Contract #${voucher.id}`}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{voucher.created ? new Date(voucher.created).toLocaleDateString() : '-'}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Typography variant="body2" fontWeight={500} color="success.main">
												{formatCurrency({
													value: voucher.amount || 0,
													currency: activity.currency,
												})}
											</Typography>
										</TableCell>
										<TableCell>
											<Chip
												label={getVoucherStatusLabel(voucher.status)}
												size="small"
												color={
													voucher.status === 'VERIFIED' || voucher.status === 'REDEEMED'
														? 'success'
														: 'default'
												}
											/>
										</TableCell>
									</TableRow>
								))}
							{activitiesWithPayments.flatMap((a) => a.vouchers).length === 0 && (
								<TableRow>
									<TableCell colSpan={5}>
										<Typography variant="body2" color="text.secondary" align="center" py={2}>
											{t('spendPlan.activityMonitoring.noPaymentsYet')}
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		</Box>
	);

	// Render timeline & charts tab
	const renderTimelineTab = () => (
		<Box>
			<Grid container spacing={3}>
				{/* Budget vs Spent Timeline */}
				<Grid item xs={12} lg={8}>
					<Paper sx={{ p: 3, borderRadius: 2 }}>
						<Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
							{t('spendPlan.activityMonitoring.budgetVsSpent')}
						</Typography>
						<ResponsiveContainer width="100%" height={350}>
							<AreaChart data={timelineData}>
								<CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
								<XAxis
									dataKey="month"
									tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
								/>
								<YAxis
									tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
									tickFormatter={(value) =>
										formatCurrency({ value, currency: defaultCurrency, notation: 'compact' })
									}
								/>
								<RechartsTooltip
									formatter={(value: number) =>
										formatCurrency({ value, currency: defaultCurrency })
									}
									contentStyle={{
										backgroundColor: theme.palette.background.paper,
										border: `1px solid ${theme.palette.divider}`,
										borderRadius: 8,
									}}
								/>
								<Legend />
								<Area
									type="monotone"
									dataKey="budget"
									name={t('spendPlan.activityMonitoring.budget')}
									stroke={theme.palette.primary.main}
									fill={alpha(theme.palette.primary.main, 0.2)}
									strokeWidth={2}
								/>
								<Area
									type="monotone"
									dataKey="spent"
									name={t('spendPlan.activityMonitoring.spent')}
									stroke={theme.palette.success.main}
									fill={alpha(theme.palette.success.main, 0.2)}
									strokeWidth={2}
								/>
							</AreaChart>
						</ResponsiveContainer>
					</Paper>
				</Grid>

				{/* Status Distribution */}
				<Grid item xs={12} lg={4}>
					<Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
						<Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
							{t('spendPlan.activityMonitoring.statusDistribution')}
						</Typography>
						<ResponsiveContainer width="100%" height={300}>
							<PieChart>
								<Pie
									data={statusDistribution}
									cx="50%"
									cy="50%"
									innerRadius={60}
									outerRadius={100}
									paddingAngle={2}
									dataKey="value"
									label={({ name, percent }) =>
										`${getStatusLabel(name, t)} (${(percent * 100).toFixed(0)}%)`
									}>
									{statusDistribution.map((entry, index) => (
										<Cell key={`cell-${index}`} fill={getStatusColor(entry.name)} />
									))}
								</Pie>
								<RechartsTooltip
									contentStyle={{
										backgroundColor: theme.palette.background.paper,
										border: `1px solid ${theme.palette.divider}`,
										borderRadius: 8,
									}}
								/>
							</PieChart>
						</ResponsiveContainer>
					</Paper>
				</Grid>

				{/* Activity Progress Timeline */}
				<Grid item xs={12}>
					<Paper sx={{ p: 3, borderRadius: 2 }}>
						<Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
							{t('spendPlan.activityMonitoring.activityProgress')}
						</Typography>
						<ResponsiveContainer width="100%" height={300}>
							<BarChart data={timelineData}>
								<CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
								<XAxis
									dataKey="month"
									tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
								/>
								<YAxis tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
								<RechartsTooltip
									contentStyle={{
										backgroundColor: theme.palette.background.paper,
										border: `1px solid ${theme.palette.divider}`,
										borderRadius: 8,
									}}
								/>
								<Legend />
								<Bar
									dataKey="activities"
									name={t('spendPlan.activityMonitoring.activitiesCount')}
									fill={theme.palette.info.main}
									radius={[4, 4, 0, 0]}
								/>
							</BarChart>
						</ResponsiveContainer>
					</Paper>
				</Grid>

				{/* Payment Trend */}
				<Grid item xs={12} md={6}>
					<Paper sx={{ p: 3, borderRadius: 2 }}>
						<Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
							{t('spendPlan.activityMonitoring.paymentTrend')}
						</Typography>
						<ResponsiveContainer width="100%" height={250}>
							<LineChart data={timelineData}>
								<CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
								<XAxis
									dataKey="month"
									tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
								/>
								<YAxis
									tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
									tickFormatter={(value) =>
										formatCurrency({ value, currency: defaultCurrency, notation: 'compact' })
									}
								/>
								<RechartsTooltip
									formatter={(value: number) =>
										formatCurrency({ value, currency: defaultCurrency })
									}
									contentStyle={{
										backgroundColor: theme.palette.background.paper,
										border: `1px solid ${theme.palette.divider}`,
										borderRadius: 8,
									}}
								/>
								<Line
									type="monotone"
									dataKey="spent"
									name={t('spendPlan.activityMonitoring.paymentsMade')}
									stroke={theme.palette.success.main}
									strokeWidth={3}
									dot={{ fill: theme.palette.success.main, strokeWidth: 2, r: 4 }}
								/>
							</LineChart>
						</ResponsiveContainer>
					</Paper>
				</Grid>

				{/* Budget Utilization */}
				<Grid item xs={12} md={6}>
					<Paper sx={{ p: 3, borderRadius: 2 }}>
						<Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
							{t('spendPlan.activityMonitoring.budgetUtilization')}
						</Typography>
						<Stack spacing={3}>
							{activitiesWithPayments.slice(0, 5).map((activity) => (
								<Box key={activity.id}>
									<Stack
										direction="row"
										justifyContent="space-between"
										alignItems="center"
										sx={{ mb: 1 }}>
										<Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: '60%' }}>
											{activity.name}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{activity.paymentProgress.toFixed(1)}%
										</Typography>
									</Stack>
									<LinearProgress
										variant="determinate"
										value={Math.min(activity.paymentProgress, 100)}
										sx={{
											height: 10,
											borderRadius: 5,
											bgcolor: alpha(theme.palette.primary.main, 0.1),
											'& .MuiLinearProgress-bar': {
												borderRadius: 5,
												background:
													activity.paymentProgress >= 100
														? `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
														: activity.paymentProgress >= 75
															? `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`
															: activity.paymentProgress >= 50
																? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
																: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
											},
										}}
									/>
								</Box>
							))}
						</Stack>
					</Paper>
				</Grid>
			</Grid>
		</Box>
	);

	return (
		<Box>
			{/* Header */}
			<HeaderListingPage
				icon={IconTimeline}
				title={t('spendPlan.activityMonitoring.title')}
				subtitle={t('spendPlan.activityMonitoring.subtitle')}
				variant="activity"
			/>

			{/* Content */}
			<Box sx={{ p: 3 }}>
				{/* Summary Cards */}
				{renderSummaryCards()}

				{/* Tabs - Full Width */}
				<Tabs
					value={tabValue}
					onChange={handleTabChange}
					sx={{
						borderBottom: 1,
						borderColor: 'divider',
						mb: 3,
						'& .MuiTab-root': {
							textTransform: 'none',
							fontWeight: 500,
							minHeight: 48,
						},
					}}>
					<Tab
						icon={<IconActivity sx={{ fontSize: 20 }} />}
						iconPosition="start"
						label={t('spendPlan.activityMonitoring.tabOverview')}
					/>
					<Tab
						icon={<IconGantt sx={{ fontSize: 20 }} />}
						iconPosition="start"
						label={t('spendPlan.activityMonitoring.tabTimelineView')}
					/>
					<Tab
						icon={<IconTimeline sx={{ fontSize: 20 }} />}
						iconPosition="start"
						label={t('spendPlan.activityMonitoring.tabTimeline')}
					/>
				</Tabs>

				{/* Tab Content */}
				<TabPanel value={tabValue} index={0}>
					{renderOverviewTab()}
				</TabPanel>
				<TabPanel value={tabValue} index={1}>
					{renderGanttTab()}
				</TabPanel>
				<TabPanel value={tabValue} index={2}>
					{renderTimelineTab()}
				</TabPanel>
			</Box>
		</Box>
	);
};
